import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import ContactHero from './components/ContactHero';
import InquiryTypeSelector from './components/InquiryTypeSelector';
import ContactMethodsGrid from './components/ContactMethodsGrid';
import ContactForm from './components/ContactForm';
import OfficeLocations from './components/OfficeLocations';
import FAQSection from './components/FAQSection';
import SocialMediaLinks from './components/SocialMediaLinks';
import Icon from '../../components/AppIcon';

const Contact = () => {
  const { t } = useTranslation();
  const [selectedInquiryType, setSelectedInquiryType] = useState('club');
  const [selectedContactMethod, setSelectedContactMethod] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInquiryTypeChange = (type) => {
    setSelectedInquiryType(type);
    const formSection = document.getElementById('contact-form-section');
    if (formSection) {
      formSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleContactMethodSelect = (method) => {
    setSelectedContactMethod(method);
    
    switch (method) {
      case 'phone':
        window.location.href = 'tel:+442079460958';
        break;
      case 'email':
        window.location.href = 'mailto:contact@prox11market.com';
        break;
      case 'whatsapp':
        window.open('https://wa.me/447700900123', '_blank');
        break;
      case 'chat': alert('Live chat feature will be available soon. Please use email or phone for immediate assistance.');
        break;
      case 'video': case'office':
        const formSection = document.getElementById('contact-form-section');
        if (formSection) {
          formSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - ProX11Market | Professional Football Talent Management</title>
        <meta 
          name="description" 
          content="Contact ProX11Market for professional football talent management services. Connect with our team through multiple channels including phone, email, video consultation, and office visits across Europe and Africa." 
        />
        <meta 
          name="keywords" 
          content="contact ProX11Market, football talent agency contact, player representation inquiry, club scouting services, technical staff placement, partnership opportunities, consultation booking" 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <ContactHero />
          
          <InquiryTypeSelector 
            selectedType={selectedInquiryType}
            onTypeChange={handleInquiryTypeChange}
          />
          
          <ContactMethodsGrid onMethodSelect={handleContactMethodSelect} />
          
          <section id="contact-form-section" className="py-12 md:py-16 lg:py-20 bg-background">
            <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full mb-4">
                  <Icon name="Send" size={16} className="text-primary" />
                  <span className="text-xs md:text-sm font-medium text-primary">Get in Touch</span>
                </div>
                
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
                  Send Us a Message
                </h2>
                
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
                  Fill out the form below and our specialized team will respond within 24 hours based on your urgency level.
                </p>
              </div>

              <ContactForm inquiryType={selectedInquiryType} />
            </div>
          </section>
          
          <OfficeLocations />
          
          <FAQSection />
          
          <SocialMediaLinks />
          
          <section className="py-12 md:py-16 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
              <Icon name="Headphones" size={48} className="mx-auto mb-6 opacity-90" />
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline mb-4">
                24/7 Support Available
              </h2>
              <p className="text-base md:text-lg lg:text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                Our global team ensures round-the-clock support for urgent inquiries. Whether you're in Europe or Africa, we're here to assist you with professional, FIFA-compliant guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+442079460958"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-white text-primary rounded-lg font-medium text-sm md:text-base hover:bg-white/90 transition-colors"
                >
                  <Icon name="Phone" size={20} />
                  Call UK Office
                </a>
                <a
                  href="tel:+2341460234"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg font-medium text-sm md:text-base hover:bg-white/20 transition-colors"
                >
                  <Icon name="Phone" size={20} />
                  Call African Hub
                </a>
              </div>
            </div>
          </section>
        </main>

        <footer className="bg-card border-t border-border py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Trophy" size={24} className="text-primary" />
                  </div>
                  <span className="text-xl font-bold font-headline text-foreground">ProX11Market</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Connecting African football excellence with European opportunities through professional, FIFA-compliant processes.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li><a href="/homepage" className="text-sm text-muted-foreground hover:text-primary transition-colors">Home</a></li>
                  <li><a href="/player-profiles" className="text-sm text-muted-foreground hover:text-primary transition-colors">Players</a></li>
                  <li><a href="/services-overview" className="text-sm text-muted-foreground hover:text-primary transition-colors">Services</a></li>
                  <li><a href="/success-stories" className="text-sm text-muted-foreground hover:text-primary transition-colors">Success Stories</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li><a href="/compliance-center" className="text-sm text-muted-foreground hover:text-primary transition-colors">Compliance</a></li>
                  <li><a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Privacy Policy</a></li>
                  <li><a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Terms of Service</a></li>
                  <li><a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Cookie Policy</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground mb-4">Contact</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Mail" size={14} />
                    <span>contact@prox11market.com</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Phone" size={14} />
                    <span>+44 20 7946 0958</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} />
                    <span>London & Lagos</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                &copy; {new Date()?.getFullYear()} ProX11Market. All rights reserved. FIFA Compliant Services.
              </p>
            </div>
          </div>
        </footer>

        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-elevated flex items-center justify-center hover:bg-primary/90 transition-all duration-300 hover:scale-110 z-50"
            aria-label="Scroll to top"
          >
            <Icon name="ArrowUp" size={24} />
          </button>
        )}
      </div>
    </>
  );
};

export default Contact;